import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import DistributionGridApi from "../../../../../infrastructure/api/distributionGrid";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import { canManageDistributionTeam } from "../../../../utils/authRoleHelpers";
import EDCBreadcrumbs, { EDC_MANAGEMENT_CRUMB, EDC_ROOT_CRUMB } from "../../EDCBreadcrumbs";
import EDCFormDialog from "../../EDCFormDialog";
import EDCListCardHeader from "../../EDCListCardHeader";
import { EDC_PATHS } from "../../edcPaths";
import useDistributionPortalContext, { pick } from "../../useDistributionPortalContext";
import {
  SUBSTATION_KINDS,
  listOf,
  runDelete,
  toApiDate,
  toDateValue,
  toFieldValue,
  toNumberOrNull,
  useGridLookups,
} from "../grid/gridShared";

const emptySubstation = {
  id: null,
  name: "",
  latinName: "",
  code: "",
  igmcCode: "",
  gisCode: "",
  distributionAreaId: "",
  cityId: null,
  kind: 0,
  totalCapacityMva: "",
  actDate: null,
  dismandDate: null,
  latitude: "",
  longitude: "",
};

const toForm = (s) => ({
  id: pick(s, "id", "Id"),
  name: pick(s, "name", "Name") || "",
  latinName: pick(s, "latinName", "LatinName") || "",
  code: pick(s, "code", "Code") || "",
  igmcCode: pick(s, "igmcCode", "IgmcCode") || "",
  gisCode: pick(s, "gisCode", "GisCode") || "",
  distributionAreaId: toFieldValue(pick(s, "distributionAreaId", "DistributionAreaId")),
  cityId: pick(s, "cityId", "CityId") ?? null,
  kind: Number(pick(s, "kind", "Kind") ?? 0),
  totalCapacityMva: toFieldValue(pick(s, "totalCapacityMva", "TotalCapacityMva")),
  actDate: toDateValue(pick(s, "actDate", "ActDate")),
  dismandDate: toDateValue(pick(s, "dismandDate", "DismandDate")),
  latitude: toFieldValue(pick(s, "latitude", "Latitude") || ""),
  longitude: toFieldValue(pick(s, "longitude", "Longitude") || ""),
});

const validateForm = (form) => {
  if (!String(form.name || "").trim()) return "نام ایستگاه الزامی است";
  if (!form.cityId) return "شهر محل ایستگاه را انتخاب کنید";
  const capacity = toNumberOrNull(form.totalCapacityMva);
  if (capacity != null && capacity < 0) return "ظرفیت ایستگاه نامعتبر است";
  return null;
};

const EDCSubstationsPage = () => {
  const { context, companyId, companyName, isCompanyAdmin, setSelectedCompanyId, loading, error } =
    useDistributionPortalContext();
  const { lookups, reloadLookups } = useGridLookups(companyId, isCompanyAdmin);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [areaFilter, setAreaFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadItems = useCallback(async () => {
    if (!companyId || !isCompanyAdmin) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    try {
      setItems(listOf(await DistributionGridApi.listSubstations(companyId)));
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت ایستگاه‌ها");
    } finally {
      setItemsLoading(false);
    }
  }, [companyId, isCompanyAdmin]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const rows = useMemo(
    () =>
      items
        .map((s) => ({
          raw: s,
          id: pick(s, "id", "Id"),
          name: pick(s, "name", "Name") || "—",
          code: pick(s, "code", "Code") || "",
          distributionAreaId: pick(s, "distributionAreaId", "DistributionAreaId"),
          areaName: pick(s, "distributionAreaName", "DistributionAreaName") || "",
          cityName: pick(s, "cityName", "CityName") || "",
          kind: Number(pick(s, "kind", "Kind") ?? 0),
          kindTitle: pick(s, "kindTitle", "KindTitle") || "",
          totalCapacityMva: Number(pick(s, "totalCapacityMva", "TotalCapacityMva") || 0),
          feederCount: Number(pick(s, "feederCount", "FeederCount") || 0),
          actDate: pick(s, "actDate", "ActDate"),
        }))
        .filter((r) => {
          if (areaFilter === "none" && r.distributionAreaId) return false;
          if (areaFilter && areaFilter !== "none" && String(r.distributionAreaId) !== areaFilter) return false;
          if (kindFilter !== "" && String(r.kind) !== kindFilter) return false;
          return true;
        }),
    [items, areaFilter, kindFilter]
  );

  const headCells = [
    { id: "name", label: "نام ایستگاه" },
    { id: "code", label: "کد دیسپاچینگ", render: (v) => v || "—" },
    { id: "areaName", label: "امور", render: (v) => v || "—" },
    { id: "cityName", label: "شهر", render: (v) => v || "—" },
    { id: "kindTitle", label: "نوع", type: "chip" },
    { id: "totalCapacityMva", label: "ظرفیت (MVA)", render: (v) => (v ? v.toLocaleString("fa-IR") : "—") },
    { id: "feederCount", label: "فیدرها" },
    { id: "actDate", label: "شروع بهره‌برداری", type: "date" },
  ];

  const setField = (field, value) => setDialog((s) => ({ ...s, [field]: value }));

  const save = async () => {
    if (!companyId || !dialog) return;
    const validation = validateForm(dialog);
    if (validation) {
      toast.error(validation);
      return;
    }
    setSaving(true);
    try {
      await DistributionGridApi.saveSubstation({
        id: dialog.id || 0,
        distributionCompanyId: Number(companyId),
        distributionAreaId: toNumberOrNull(dialog.distributionAreaId),
        name: dialog.name.trim(),
        latinName: dialog.latinName?.trim() || null,
        code: dialog.code?.trim() || null,
        igmcCode: dialog.igmcCode?.trim() || null,
        gisCode: dialog.gisCode?.trim() || null,
        kind: Number(dialog.kind) || 0,
        cityId: dialog.cityId,
        totalCapacityMva: toNumberOrNull(dialog.totalCapacityMva) ?? 0,
        actDate: toApiDate(dialog.actDate),
        dismandDate: toApiDate(dialog.dismandDate),
        latitude: toNumberOrNull(dialog.latitude),
        longitude: toNumberOrNull(dialog.longitude),
      });
      toast.success(dialog.id ? "تغییرات ایستگاه ذخیره شد" : "ایستگاه ایجاد شد");
      setDialog(null);
      await Promise.all([loadItems(), reloadLookups()]);
    } catch (e) {
      toast.error(e?.message || "خطا در ذخیره ایستگاه");
    } finally {
      setSaving(false);
    }
  };

  const refresh = () => Promise.all([loadItems(), reloadLookups()]);

  if (!canManageDistributionTeam()) return <Navigate to={EDC_PATHS.root} replace />;
  if (context && !isCompanyAdmin) return <Navigate to={EDC_PATHS.root} replace />;

  const busy = loading || itemsLoading;
  const selectedCity = lookups.cities.find((c) => c.id === dialog?.cityId) || null;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <EDCBreadcrumbs
          crumbs={[
            EDC_ROOT_CRUMB,
            EDC_MANAGEMENT_CRUMB,
            { title: "ایستگاه‌های فوق توزیع", to: EDC_PATHS.substations },
          ]}
        />
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <Card>
          <EDCListCardHeader
            title="ایستگاه‌های فوق توزیع"
            subheader={
              companyName
                ? `${companyName} — ${items.length} ایستگاه`
                : "ایستگاه‌های فوق توزیع (۶۳/۲۰ کیلوولت) شرکت توزیع"
            }
            context={context}
            onCompanyChange={setSelectedCompanyId}
            onCreate={() => setDialog({ ...emptySubstation })}
            createDisabled={!companyId}
            onRefresh={refresh}
            busy={busy}
          />
          <CardContent>
            {companyId && !loading && lookups.areas.length === 0 ? (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button component={RouterLink} to={EDC_PATHS.areas} size="small">
                    تعریف امور
                  </Button>
                }
              >
                برای نسبت دادن ایستگاه به امور، ابتدا امورهای شرکت را تعریف کنید.
              </Alert>
            ) : null}

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="امور"
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">همه امور</MenuItem>
                <MenuItem value="none">بدون امور</MenuItem>
                {lookups.areas.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="نوع ایستگاه"
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">همه</MenuItem>
                {SUBSTATION_KINDS.map((k) => (
                  <MenuItem key={k.value} value={String(k.value)}>
                    {k.title}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <EnhancedTable
              rowKey="id"
              rows={rows}
              headCells={headCells}
              pageSize={100}
              loading={busy}
              defaultSort={{ orderBy: "name", order: "asc" }}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: (row) => setDialog(toForm(row.raw)),
                },
                {
                  icon: <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "حذف",
                  color: "error",
                  onClick: (row) => setDeleteTarget(row),
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>

      <EDCFormDialog
        open={Boolean(dialog)}
        title={dialog?.id ? "ویرایش ایستگاه فوق توزیع" : "ایجاد ایستگاه فوق توزیع"}
        saving={saving}
        onClose={() => setDialog(null)}
        onSave={save}
      >
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={700}>
            مشخصات ایستگاه
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="نام ایستگاه"
            value={dialog?.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            required
            fullWidth
            inputProps={{ maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="نام لاتین"
            value={dialog?.latinName || ""}
            onChange={(e) => setField("latinName", e.target.value)}
            fullWidth
            inputProps={{ dir: "ltr", maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="نوع ایستگاه"
            value={dialog?.kind ?? 0}
            onChange={(e) => setField("kind", Number(e.target.value))}
            fullWidth
          >
            {SUBSTATION_KINDS.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="امور"
            value={dialog?.distributionAreaId ?? ""}
            onChange={(e) => setField("distributionAreaId", e.target.value)}
            fullWidth
          >
            <MenuItem value="">بدون امور</MenuItem>
            {lookups.areas.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={lookups.cities}
            value={selectedCity}
            getOptionLabel={(o) => o.title}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => setField("cityId", v?.id ?? null)}
            noOptionsText="شهری یافت نشد"
            renderInput={(params) => <TextField {...params} label="شهر" required />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="ظرفیت کل (مگاولت‌آمپر)"
            type="number"
            value={dialog?.totalCapacityMva ?? ""}
            onChange={(e) => setField("totalCapacityMva", e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: "any", dir: "ltr" }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            کدها
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="کد دیسپاچینگ"
            value={dialog?.code || ""}
            onChange={(e) => setField("code", e.target.value)}
            fullWidth
            inputProps={{ dir: "ltr", maxLength: 10 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="کد IGMC"
            value={dialog?.igmcCode || ""}
            onChange={(e) => setField("igmcCode", e.target.value)}
            fullWidth
            inputProps={{ dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="کد GIS"
            value={dialog?.gisCode || ""}
            onChange={(e) => setField("gisCode", e.target.value)}
            fullWidth
            inputProps={{ dir: "ltr" }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            بهره‌برداری و موقعیت
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <JalaliDatePicker
            label="تاریخ شروع بهره‌برداری"
            value={dialog?.actDate || null}
            onChange={(v) => setField("actDate", toDateValue(v))}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <JalaliDatePicker
            label="تاریخ پایان بهره‌برداری"
            value={dialog?.dismandDate || null}
            onChange={(v) => setField("dismandDate", toDateValue(v))}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="عرض جغرافیایی"
            type="number"
            value={dialog?.latitude ?? ""}
            onChange={(e) => setField("latitude", e.target.value)}
            fullWidth
            inputProps={{ step: "any", dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="طول جغرافیایی"
            type="number"
            value={dialog?.longitude ?? ""}
            onChange={(e) => setField("longitude", e.target.value)}
            fullWidth
            inputProps={{ step: "any", dir: "ltr" }}
          />
        </Grid>
      </EDCFormDialog>

      <DeleteConfirm
        open={Boolean(deleteTarget)}
        message={`ایستگاه «${deleteTarget?.name || ""}» حذف شود؟ ایستگاهی که فیدر دارد قابل حذف نیست.`}
        onClose={(loadAgain) => {
          setDeleteTarget(null);
          if (loadAgain === true) refresh();
        }}
        onSubmit={() =>
          runDelete(
            () => DistributionGridApi.deleteSubstation({ distributionCompanyId: Number(companyId), id: deleteTarget.id }),
            "ایستگاه حذف شد"
          )
        }
      />
    </Grid>
  );
};

export default EDCSubstationsPage;
